import { ShareButton } from '../sharing/Sharing';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import useVoyagerStore from '../stores/useVoyagerStore';
import ChatComposer from '../components/LandingAgent/ChatComposer';
import TurnBlock from '../components/LandingAgent/TurnBlock';
import { deltaText, reviewOfSection, sectionTitle } from '../stores/slices/reviewsSlice';
import { formatKpiValue } from '../components/agent-common/formatKpiValue';
import WalkRail from '../components/Ladder/WalkRail';
import { SidePanelHost, SidePanelSlot } from '../components/LandingAgent/simple/ResultPanel';

/** Where each tab was scrolled to, so switching back lands where you left. */
const scrollPositions = new Map<string, number>();

/**
 * One thread tab (#182): the thread bar (title, perspective, ★, ✕), the turn
 * list — the same `TurnBlock`/`AnswerCard` as before — and the docked
 * composer. Scroll position and composer draft are the tab's own.
 */
export default function ThreadView({ threadId }: { threadId: string }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const turns = useVoyagerStore((s) => s.landingAgentTurns);
  const status = useVoyagerStore((s) => s.landingAgentStatus);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const starThread = useVoyagerStore((s) => s.starThread);
  const closeThread = useVoyagerStore((s) => s.closeThread);
  const loadThread = useVoyagerStore((s) => s.loadThread);
  // A walk opened from a review section (#189): the review it came from and
  // the number it explains ride along, so the tab says where it was opened
  // from and what is pre-loaded — the mock's banner and its right rail.
  const origin = useVoyagerStore((s) => (thread?.sourceKind === 'review_section' ? reviewOfSection(s.savedThreads, thread.sourceId) : null));
  // a question asked from a review's own composer (#568): the review it came from
  const askedFrom = useVoyagerStore((s) => (thread?.sourceKind === 'review' ? s.savedThreads.find((t) => t.id === thread.sourceId && t.kind === 'doc') || null : null));
  // the objective the thread is asked inside (#766), as the viewer can see it now; none shown when they cannot
  const objective = useVoyagerStore((s) => (thread?.objectiveId ? s.agents.find((a) => a.id === thread.objectiveId) || null : null));
  // The latest walk in this thread: the rail (evidence policy, risk tolerance,
  // the rungs, the review it can be pinned to) belongs to every walk, not only
  // one opened from a review.
  // A walk that re-runs (Keep digging, another bar) has its result cleared
  // while it runs: `isWalk` keeps the rail with it, also for a walk persisted
  // before the flag existed.
  // the simplified app (#1508) parks the rail: shown only on a `?parked=1` link to this thread
  const railParked = useVoyagerStore((s) => s.threadRailParked);
  const showRail = railParked === threadId;
  const latestWalk = [...turns].reverse().find((t) => !!t.result?.diagnose || (t.status === 'running' && (!!t.isWalk || !!t.intent))) || null;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // where the open answer's panel lands (its tabs: Data, SQL, Trace…)
  const [sideAt, setSideAt] = React.useState<HTMLDivElement | null>(null);
  const lastTurnCount = useRef(turns.length);
  // A `?turn=` link (#472): that turn is scrolled to and marked; one no longer here is said, not ignored.
  const anchor = useVoyagerStore((s) => (s.threadTurnAnchor?.threadId === threadId ? s.threadTurnAnchor.turnId : null));
  const anchorMissing = !!anchor && !turns.some((t) => t.id === anchor);

  // Restore this tab's scroll on mount; remember it as it changes.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = scrollPositions.get(threadId) ?? el.scrollHeight;
  }, [threadId]);

  // A new turn or a status change scrolls to the bottom, as before.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (turns.length !== lastTurnCount.current || status === 'running') {
      el.scrollTop = el.scrollHeight;
      lastTurnCount.current = turns.length;
    }
  }, [turns.length, status]);

  useEffect(() => {
    if (!anchor) return;
    const turnEls = scrollRef.current?.querySelectorAll<HTMLElement>('[data-turn-id]') || [];
    Array.from(turnEls).find((el) => el.dataset.turnId === anchor)?.scrollIntoView?.({ block: 'start' });
  }, [anchor, threadId]);

  const perspectiveName = thread?.perspectiveId
    ? (perspectives.find((p) => p.id === thread.perspectiveId)?.name || thread.perspectiveId)
    : null;

  return (
    <Box data-testid="thread-view" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      {thread && (
        <Box
          data-testid="thread-bar"
          sx={{ px: { xs: 2, sm: 3 }, py: 1.25, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center', bgcolor: 'background.paper', flexShrink: 0 }}
        >
          <Box sx={{ width: '100%', maxWidth: 760, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {objective && (
                // the objective the thread is asked inside (#766), above its title; it opens the objective's space
                <Box component="button" type="button" data-testid="thread-objective" onClick={() => useVoyagerStore.getState().openObjectiveSpace(objective.id)}
                  sx={{ display: 'block', maxWidth: '100%', p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 600, color: 'var(--accent-ink)', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Objective · {objective.objective.label || objective.objective.metric.split('.').pop() || objective.objective.metric}
                </Box>
              )}
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {thread.title}
              </Typography>
            </Box>
            <Chip label={perspectiveName || 'Auto'} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />
            <Tooltip title={thread.starred ? 'Unpin' : 'Pin this thread'}>
              <IconButton
                size="small"
                aria-label={thread.starred ? 'Unstar thread' : 'Star thread'}
                onClick={() => starThread(thread.id, !thread.starred)}
                sx={{ color: thread.starred ? 'warning.main' : 'text.disabled' }}
              >
                {thread.starred ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <ShareButton threadId={thread.id} />
            <Tooltip title="Close thread — it moves to History">
              <IconButton
                size="small"
                aria-label="Close thread"
                onClick={() => closeThread(thread.id)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
      {origin && (
        <Box data-testid="thread-origin" sx={{ px: 3, py: 0.75, borderBottom: 1, borderColor: 'var(--warn)', bgcolor: 'var(--warn-tint)', display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 1060, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'var(--warn)', fontWeight: 600 }}>
              Opened from {origin.review.title}
              {origin.section.window?.start ? ` · Week of ${String(origin.section.window.start).slice(0, 10)}` : ''}
              {' — '}{sectionTitle(origin.section)}
              {origin.section.kind === 'claim' ? ` ${formatKpiValue(origin.section.value ?? origin.section.claim?.value, origin.section.claim?.format)}` : ''}
              {deltaText(origin.section.delta, origin.section.previous) ? ` ${deltaText(origin.section.delta, origin.section.previous)}` : ''}
              {' · context pre-loaded'}
            </Typography>
            <Button size="small" variant="text" onClick={() => loadThread(origin.review.id)} sx={{ ml: 'auto', py: 0, minHeight: 0, fontSize: '0.72rem' }}>Open the review</Button>
          </Box>
        </Box>
      )}
      {!origin && askedFrom && (
        <Box data-testid="thread-origin" sx={{ px: 3, py: 0.75, borderBottom: 1, borderColor: 'var(--warn)', bgcolor: 'var(--warn-tint)', display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 1060, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'var(--warn)', fontWeight: 600 }}>Asked from {askedFrom.title} · scoped to its numbers</Typography>
            <Button size="small" variant="text" onClick={() => loadThread(askedFrom.id)} sx={{ ml: 'auto', py: 0, minHeight: 0, fontSize: '0.72rem' }}>Open the review</Button>
          </Box>
        </Box>
      )}
      {/* the turns and the composer, with the open answer's panel on their right (full screen on a phone) */}
      <SidePanelSlot.Provider value={sideAt}>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
      <Box sx={{ flex: '999 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        ref={scrollRef}
        onScroll={(e) => scrollPositions.set(threadId, (e.currentTarget as HTMLDivElement).scrollTop)}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center', gap: 3, '@media (max-width:1100px)': { flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' } }}
      >
        <Box sx={{ width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {anchorMissing && (
            <Alert severity="info" data-testid="turn-anchor-missing">The linked turn is no longer in this thread.</Alert>
          )}
          {turns.map((t, i) => (
            <Box key={t.id} data-turn-id={t.id} data-anchored={t.id === anchor ? 'true' : undefined}
                 sx={t.id === anchor ? { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: 6, borderRadius: 1 } : undefined}>
              <TurnBlock turn={t} isLatest={i === turns.length - 1} />
            </Box>
          ))}
        </Box>
        {thread && showRail && (latestWalk || turns.some((t) => t.status === 'done' && t.result)) && (
          // the newest ANSWERED turn carries the pinnable number — not a failed or cancelled last turn
          <WalkRail threadId={thread.id} turn={latestWalk} latestTurn={[...turns].reverse().find((t) => t.status === 'done' && !!t.result) || null} />
        )}
      </Box>
      <Box sx={{ px: 2, py: 2, borderTop: 0, borderColor: 'divider', display: 'flex', justifyContent: 'center', flexShrink: 0, bgcolor: 'background.default' }}>
        <Box sx={{ width: '100%', maxWidth: 760 }}>
          <ChatComposer variant="docked" draftKey={threadId} />
        </Box>
      </Box>
      </Box>
      <SidePanelHost ref={setSideAt} />
      </Box>
      </SidePanelSlot.Provider>
    </Box>
  );
}
